import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import StudentSidebar from '../components/StudentSidebar.js';
import '../styles/StudentProfile.css';
import { parseISO, format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { BookOpen, Cake, Camera, Clock, KeyRound, Mail, TrendingUp, User, Users, Settings, Edit3, IdCard, FileCheck, ChevronDown, ChevronUp, Phone, MapPin, Briefcase } from 'lucide-react';
import EditProfileModal from '../components/EditProfileModal.js';
import { roomDetailLines } from '../utils/roomDisplay.js';


export default function StudentProfile() {
    const [searchParams] = useSearchParams();
    // State for student profile
    const [profile, setProfile] = useState(null);
    // State to load profile
    const [loadingProfile, setLoadingProfile] = useState(true);
    // State for student stats
    const [stats, setStats] = useState(null);
    // State to load stats
    const [loadingStats, setLoadingStats] = useState(true);
    // State for active tab - check URL params for initial tab
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
    // State for assignments stats
    const [assignmentStats, setAssignmentStats] = useState(null);
    // State to load assignments stats
    const [loadingAssignmentStats, setLoadingAssignmentStats] = useState(true);
    // State to hold upcoming sessions
    const [upcomingSessions, setUpcomingSessions] = useState([]);
    // State to load upcoming sessions
    const [loadingUpcomingSessions, setLoadingUpcomingSessions] = useState(true);
    // State for modules
    const [modules, setModules] = useState([]);
    // State to load modules
    const [loadingModules, setLoadingModules] = useState(true);
    // State to hold user
    const [user, setUser] = useState(null);
    // State to load user
    const [loadingUser, setLoadingUser] = useState(true);
    // State to control edit modal visibility
    const [showEditModal, setShowEditModal] = useState(false);
    // State for terms acceptance
    const [termsStatus, setTermsStatus] = useState({
        privacy_policy_accepted: false,
        terms_accepted: false,
        age_of_majority_confirmed: false
    });
    // State to load terms status
    const [loadingTermsStatus, setLoadingTermsStatus] = useState(true);
    // State for expanded sections
    const [expandedSections, setExpandedSections] = useState({
        terms: false,
        privacy: false,
        age: false
    });
    // State for consent notification
    const [showConsentNotice, setShowConsentNotice] = useState(false);
    // State for profile photo
    const [photoUrl, setPhotoUrl] = useState(null);
    const photoUrlRef = useRef(null);

    // Function to get grade color class
    const getGradeClass = (rate) => {
        if (rate >= 4.5) return 'grade-green-overall';
        if (rate >= 3.0) return 'grade-yellow-overall';
        return 'grade-red-overall';
    };
    // Function to get progress bar color class for average grade
    const getProgressBarClass = (rate) => {
        if (rate < 3.5) return '#dc2626';
        if (rate < 4.5) return '#eab308';
        return '#16a34a';
    };
    // Function to get color for attendance
    const getAttendanceClass = (rate) => {
        if (rate >= 90) return 'attendance-green-overall';
        if (rate >= 75) return 'attendance-yellow-overall';
        return 'attendance-red-overall';
    };
    // Function to get progress bar color class for attendance
    const getAttendanceProgressBarClass = (rate) => {
        if (rate >= 90) return '#16a34a';
        if (rate >= 75) return '#eab308';
        return '#dc2626';
    };

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/profile/student`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
        .then(response => {
            setProfile(response.data);
            setLoadingProfile(false);
        })
        .catch(error => {
            console.error("Error fetching profile:", error);
            setLoadingProfile(false);
        });
    }, []);

    // Fetch photo when profile is loaded
    useEffect(() => {
        if (profile) {
            axios.get(`${process.env.REACT_APP_API_URL}/profile/student/photo`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                responseType: 'blob'
            })
            .then(response => {
                const blob = response.data;
                // Revoke old URL if it exists
                if (photoUrlRef.current) {
                    URL.revokeObjectURL(photoUrlRef.current);
                }
                const url = URL.createObjectURL(blob);
                photoUrlRef.current = url;
                setPhotoUrl(url);
            })
            .catch(error => {
                // Photo doesn't exist or error loading - that's okay, use initials
                if (error.response?.status !== 404) {
                    console.error("Error fetching photo:", error);
                }
                if (photoUrlRef.current) {
                    URL.revokeObjectURL(photoUrlRef.current);
                    photoUrlRef.current = null;
                }
                setPhotoUrl(null);
            });
        }

        // Cleanup function to revoke URL when component unmounts
        return () => {
            if (photoUrlRef.current) {
                URL.revokeObjectURL(photoUrlRef.current);
            }
        };
    }, [profile]);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/profile/stats`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
        .then(response => {
            setStats(response.data);
            setLoadingStats(false);
        })
        .catch(error => {
            console.error("Error fetching stats:", error);
            setLoadingStats(false);
        });
    }, []);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/profile/assignmentStats`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
        .then(response => {
            setAssignmentStats(response.data);
            setLoadingAssignmentStats(false);
        })
        .catch(error => {
            console.error("Error fetching assignment stats:", error);
            setLoadingAssignmentStats(false);
        });
    }, []);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/sessions/studentUpcoming`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
        .then(response => {
            setUpcomingSessions(response.data);
            setLoadingUpcomingSessions(false);
        })
        .catch(error => {
            console.error("Error fetching upcoming sessions:", error);
            setLoadingUpcomingSessions(false);
        });
    }, []);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/profile/modules`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
        .then(response => {
            setModules(response.data);
            setLoadingModules(false);
        })
        .catch(error => {
            console.error("Error fetching modules:", error);
            setLoadingModules(false);
        });
    }, []);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/profile/user`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
        .then(response => {
            setUser(response.data);
            setLoadingUser(false);
        })
        .catch(error => {
            console.error("Error fetching user data:", error);
            setLoadingUser(false);
        });
    }, []);

    // Fetch terms acceptance status
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/profile/terms`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
        .then(response => {
            setTermsStatus(response.data);
            setLoadingTermsStatus(false);
        })
        .catch(error => {
            console.error("Error fetching terms status:", error);
            setLoadingTermsStatus(false);
        });
    }, []);

    useEffect(() => {
        if (loadingTermsStatus) {
            return;
        }

        const hasPendingConsents = !termsStatus.privacy_policy_accepted ||
            !termsStatus.terms_accepted ||
            !termsStatus.age_of_majority_confirmed;

        setShowConsentNotice(hasPendingConsents);
    }, [loadingTermsStatus, termsStatus]);

    // Function to handle checkbox change
    const handleTermsChange = async (field, value) => {
        // Prevent unchecking - once accepted, cannot be unchecked
        if (!value && termsStatus[field]) {
            return;
        }

        // Only allow checking (setting to true)
        if (!value) {
            return;
        }

        const newStatus = { ...termsStatus, [field]: value };
        setTermsStatus(newStatus);

        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}/profile/terms`,
                { [field]: value },
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
            );
        } catch (error) {
            console.error("Error updating terms acceptance:", error);
            // Revert on error
            setTermsStatus(termsStatus);
        }
    };

    const renderOverviewTab = () => {
        if (!stats) {
            return <div>Brak statystyk do wyświetlenia.</div>;
        }

        return (
            <div>
                <div className='overview-grid grid-2-columns'>
                    <div className='overview-card'>
                        <div className='overview-card-header'>
                            <h3 className='overview-card-title'>
                                <User size={20} />
                                Dane kursanta
                            </h3>
                            {/* <button className='btn-profile btn-secondary-profile'>
                                <Edit3 size={16} />
                                Edytuj
                            </button> */}
                        </div>
                        <div>
                            <div>
                                <div className='overview-data-row'>
                                    <Mail size={16} color='#64748b'/>
                                    <span className='overview-label'>{profile.email}</span>
                                </div>
                                <div className='overview-data-row'>
                                    <KeyRound size={16} color='#64748b'/>
                                    <span className='overview-label'>{profile.pesel || 'Brak peselu'}</span>
                                </div>
                                <div className='overview-data-row'>
                                    <Cake size={16} color='#64748b'/>
                                    <span className='overview-label'>
                                        {profile.date_of_birth
                                        ? format(parseISO(profile.date_of_birth), 'dd.MM.yyyy', { locale: pl })
                                        : 'Brak daty urodzenia'}
                                    </span>
                                </div>
                                <div className='overview-data-row'>
                                    <Users size={16} color='#64748b'/>
                                    <span className='overview-label'>{profile.group_code || 'Brak grupy'}</span>
                                </div>
                                <div className='overview-data-row'>
                                    <Phone size={16} color='#64748b'/>
                                    <span className='overview-label'>{profile.phone_number || 'Brak numeru telefonu'}</span>
                                </div>
                                <div className='overview-data-row'>
                                    <MapPin size={16} color='#64748b'/>
                                    <span className='overview-label'>{profile.address || 'Brak adresu'}</span>
                                </div>
                                <div className='overview-data-row'>
                                    <Briefcase size={16} color='#64748b'/>
                                    <span className='overview-label'>{profile.job || 'Brak zawodu'}</span>
                                </div>
                            </div>
                        </div>
                        <div className='overview-card-footer'>
                            <div className='overview-note'>
                                Numer indeksu: {profile.student_number}
                            </div>
                        </div>
                    </div>
                    {/* Progres */}
                    <div className='overview-card'>
                        <h3 className='overview-card-title'>
                            <TrendingUp size={20} />
                            Twoje postępy
                        </h3>
                        <div style={{marginBottom: '1.5rem'}}>
                            <div className='overview-progress-row-label'>
                                <span className='overview-progress-label'>Średnia ocen</span>
                                <span className={`overview-student-grade ${getGradeClass(stats.average_grade)}`}>{stats.average_grade}</span>
                            </div>
                            <div className='overview-progress-bar-background'>
                                <div 
                                    className='overview-progress-bar-fill'
                                    style={{
                                        width: `${(stats.average_grade / 5) * 100}%`,
                                        backgroundColor: getProgressBarClass(stats.average_grade)
                                    }}
                                >
                                </div>
                            </div>
                        </div>
                        <div style={{marginBottom: '1.5rem'}}>
                            <div className='overview-progress-row-label'>
                                <span className='overview-progress-label'>Frekwencja</span>
                                <span className={`overview-student-grade ${getAttendanceClass(stats.attendance_percentage)}`}>{stats.attendance_percentage}%</span>
                            </div>
                            <div className='overview-progress-bar-background'>
                                <div
                                    className='overview-progress-bar-fill'
                                    style={{
                                        width: `${stats.attendance_percentage}%`,
                                        backgroundColor: getAttendanceProgressBarClass(stats.attendance_percentage)
                                    }}
                                >
                                </div>
                            </div>
                        </div>
                        {loadingAssignmentStats ? (
                            <div>ładowanie statystyk zadań...</div>
                        ) : !assignmentStats ? (
                            <div>Brak statystyk zadań do wyświetlenia.</div>
                        ) : (
                            <div className='overview-assignments-completed'>
                                <div className='overview-assignments-item'>
                                    <div className='overview-assignments-value'>
                                        {assignmentStats.completed_assignments}
                                    </div>
                                    <div className='overview-assignments-label'>
                                        Ukończone zadania
                                    </div>
                                </div>
                                <div className='overview-assignments-item'>
                                    <div className='overview-assignments-value'>
                                        {assignmentStats.passed_exams}
                                    </div>
                                    <div className='overview-assignments-label'>
                                        Zaliczone egzaminy
                                    </div>
                                </div>
                            </div>        
                        )}
                    </div>
                </div>
                <div className='overview-card'>
                    <h3 className='overview-card-title'>
                        <Clock size={20} />
                        Nadchodzące zajęcia
                    </h3>
                    {loadingUpcomingSessions ? (
                        <div>ładowanie nadchodzących zajęć...</div>
                    ) : upcomingSessions.length === 0 ? (
                        <div>Brak nadchodzących zajęć.</div>
                    ) : (
                        <>
                            {upcomingSessions.map((session) => (
                                <div key={session.session_id} className='overview-upcoming-session-row'>
                                    <div>
                                        <div style={{fontWeight: '600', marginBottom: '4px'}}>{session.module_name}</div>
                                        <div style={{ fontSize: '14px', color: '#64748b' }}>{session.session_name}</div>
                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                                            {session.session_date} • {session.start_time} - {session.end_time}
                                            {roomDetailLines(
                                                session.room_name,
                                                session.location,
                                                session.room_comment
                                            ).map((line, i) => (
                                                <span key={i}>
                                                    {i === 0 ? ' • ' : ' · '}
                                                    <span style={{ color: '#64748b' }}>{line.label}:</span>{' '}
                                                    {line.value}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        );
    }

    const renderModulesTab = () => {
        if (loadingModules) {
            return <div>ładowanie przedmiotów...</div>;
        }

        return (
            <div className='overview-grid grid-2-columns'>
                {modules.map((module) => (
                    <div key={module.module_id} className='course-card'>
                        <div className='course-card-section'>
                            <div>
                                <h4 style={{ margin: '0 0 4px 0', fontWeight: '600' }}>{module.module_name}</h4>
                                <p className='course-lecturer'>{module.lecturers}</p>
                            </div>
                            <span 
                                className='course-status-badge'
                                style={{
                                    backgroundColor: module.status === 'aktywny' ? '#dcfce7' : '#f3f4f6',
                                    color: module.status === 'aktywny' ? '#166534' : '#6b7280'
                                }}
                            >
                                {module.status === 'aktywny' ? 'Aktywny' : 'Ukończony'}
                            </span>
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                            <div className='course-progress-bar-label'>
                                <span style={{ fontSize: '14px' }}>Postęp przedmiotu</span>
                                 <span style={{ fontSize: '14px', fontWeight: '500' }}>{module.progress_percent}%</span>
                            </div>
                            <div className='overview-progress-bar-background'>
                                <div
                                    className='overview-progress-bar-fill'
                                    style={{
                                        width: `${module.progress_percent}%`,
                                        backgroundColor: '#16a34a'
                                    }}
                                >
                                </div>
                            </div>
                        </div>
                        <div className='course-statistics-row'>
                            <div>
                                <div className='course-statistics-value'>{module.combined_avg_grade}</div>
                                <div className='course-statistics-label'>Ocena</div>
                            </div>
                            <div>
                                <div className='course-statistics-value'>{module.attendance_percent}%</div>
                                <div className='course-statistics-label'>Frekwencja</div>
                            </div>
                        </div>
                        {module.status === 'aktywny' && module.next_class_date && (
                            <div className='course-next-class'>
                                <div className='course-next-class-label'>
                                    Najbliższe zajęcia: {module.next_class_date && module.next_class_start && module.next_class_end
                                    ? `${format(parseISO(module.next_class_date), 'dd.MM.yyyy', { locale: pl })} • ${module.next_class_start.slice(0,5)} - ${module.next_class_end.slice(0,5)}`
                                    : 'Brak najbliższych zajęć'}

                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    const renderSettingsTab = () => {
        if (loadingUser) {
            return <div>ładowanie danych użytkownika...</div>;
        }

        return (
            <div className='settings-tab'>
                {/* User data section */}
                <div className='overview-card'>
                    <div className='overview-card-header'>
                        <h3 className='overview-card-title'>
                            <Settings size={20} />
                            Dane użytkownika
                        </h3>
                        <button 
                            className='btn-profile btn-secondary-profile' 
                            onClick={() => setShowEditModal(true)}
                        >
                            <Edit3 size={16} />
                            Edytuj
                        </button>
                    </div>
                    <div>
                        <div>
                            <div className='overview-data-row-settings'>
                                <Mail size={20} color='#64748b'/>
                                <span className='overview-label'>{profile.email}</span>
                            </div>
                            <div className='overview-data-row-settings'>
                                <User size={20} color='#64748b'/>
                                <span className='overview-label'>{user ? user.username : 'Ładowanie...'}</span>
                            </div>
                            <div className='overview-data-row-settings'>
                                <Cake size={20} color='#64748b'/>
                                <span className='overview-label'>
                                    {profile.date_of_birth
                                    ? format(parseISO(profile.date_of_birth), 'dd.MM.yyyy', { locale: pl })
                                    : 'Brak daty urodzenia'}
                                </span>
                            </div>
                            <div className='overview-data-row-settings'>
                                <IdCard size={20}/>
                                <span className='overview-label'>{profile.pesel || 'Brak peselu'}</span>
                            </div>
                            <div className='overview-data-row-settings'>
                                <Phone size={20} color='#64748b'/>
                                <span className='overview-label'>{profile.phone_number || 'Brak numeru telefonu'}</span>
                            </div>
                            <div className='overview-data-row-settings'>
                                <MapPin size={20} color='#64748b'/>
                                <span className='overview-label'>{profile.address || 'Brak adresu'}</span>
                            </div>
                            <div className='overview-data-row-settings'>
                                <Briefcase size={20} color='#64748b'/>
                                <span className='overview-label'>{profile.job || 'Brak zawodu'}</span>
                            </div>
                        </div>
                    </div>
                    {/* Moje zgody section */}
                    <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e2e8f0' }}>
                        <h3 className='overview-card-title profile-consents-heading'>
                            <FileCheck size={20} color='#64748b'/>
                            Moje zgody
                        </h3>
                        {loadingTermsStatus ? (
                            <div>ładowanie...</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                {/* Terms acceptance */}
                                <div>
                                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: termsStatus.terms_accepted ? 'default' : 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            className="terms-checkbox"
                                            checked={termsStatus.terms_accepted}
                                            disabled={termsStatus.terms_accepted}
                                            onChange={(e) => handleTermsChange('terms_accepted', e.target.checked)}
                                            style={{ 
                                                width: '18px', 
                                                height: '18px', 
                                                cursor: termsStatus.terms_accepted ? 'not-allowed' : 'pointer',
                                                accentColor: '#16a34a',
                                                marginTop: '2px',
                                                flexShrink: 0
                                            }}
                                        />
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span className='overview-label' style={{ fontSize: '15px' }}>Akceptuję Regulamin Systemu Arrio – PSDiA.</span>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setExpandedSections(prev => ({ ...prev, terms: !prev.terms }));
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '0',
                                                    color: '#16a34a',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {expandedSections.terms ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                            </button>
                                        </div>
                                    </label>
                                    {expandedSections.terms && (
                                        <div style={{ marginLeft: '1.9rem', marginTop: '0.75rem', paddingLeft: '1rem', borderLeft: '2px solid #e2e8f0' }}>
                                            <p className='overview-label' style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6' }}>
                                                Akceptując Regulamin Systemu Arrio – PSDiA, zawieram umowę o świadczenie usług drogą elektroniczną z Fundacją Polska Szkoła Dendrologii i Arborystyki i zobowiązuję się korzystać z Systemu zgodnie z jego postanowieniami.
                                                <a href="/system-regulations" target="_blank" style={{ color: '#16a34a', textDecoration: 'none' }}> Regulamin Systemu Arrio – PSDiA.</a>
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Privacy policy */}
                                <div>
                                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: termsStatus.privacy_policy_accepted ? 'default' : 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            className="terms-checkbox"
                                            checked={termsStatus.privacy_policy_accepted}
                                            disabled={termsStatus.privacy_policy_accepted}
                                            onChange={(e) => handleTermsChange('privacy_policy_accepted', e.target.checked)}
                                            style={{ 
                                                width: '18px', 
                                                height: '18px', 
                                                cursor: termsStatus.privacy_policy_accepted ? 'not-allowed' : 'pointer',
                                                accentColor: '#16a34a',
                                                marginTop: '2px',
                                                flexShrink: 0
                                            }}
                                        />
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span className='overview-label' style={{ fontSize: '15px' }}>Potwierdzam, że zapoznałem(am) się z Polityką prywatności Systemu Arrio – PSDiA.</span>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setExpandedSections(prev => ({ ...prev, privacy: !prev.privacy }));
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '0',
                                                    color: '#16a34a',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {expandedSections.privacy ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                            </button>
                                        </div>
                                    </label>
                                    {expandedSections.privacy && (
                                        <div style={{ marginLeft: '1.9rem', marginTop: '0.75rem', paddingLeft: '1rem', borderLeft: '2px solid #e2e8f0' }}>
                                            <p className='overview-label' style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6' }}>
                                                Przyjmuję do wiadomości zasady przetwarzania moich danych osobowych opisane w Polityce prywatności Systemu Arrio – PSDiA, w tym informacje o administratorze, celach, podstawach prawnych, odbiorcach danych oraz przysługujących mi prawach. 
                                                <a href="/privacy-policy" target="_blank" style={{ color: '#16a34a', textDecoration: 'none' }}> Polityka prywatności.</a>
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Age of majority */}
                                <div>
                                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: termsStatus.age_of_majority_confirmed ? 'default' : 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            className="terms-checkbox"
                                            checked={termsStatus.age_of_majority_confirmed}
                                            disabled={termsStatus.age_of_majority_confirmed}
                                            onChange={(e) => handleTermsChange('age_of_majority_confirmed', e.target.checked)}
                                            style={{ 
                                                width: '18px', 
                                                height: '18px', 
                                                cursor: termsStatus.age_of_majority_confirmed ? 'not-allowed' : 'pointer',
                                                accentColor: '#16a34a',
                                                marginTop: '2px',
                                                flexShrink: 0
                                            }}
                                        />
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span className='overview-label' style={{ fontSize: '15px' }}>Oświadczam, że mam ukończone 18 lat.</span>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setExpandedSections(prev => ({ ...prev, age: !prev.age }));
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '0',
                                                    color: '#16a34a',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {expandedSections.age ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                            </button>
                                        </div>
                                    </label>
                                    {expandedSections.age && (
                                        <div style={{ marginLeft: '1.9rem', marginTop: '0.75rem', paddingLeft: '1rem', borderLeft: '2px solid #e2e8f0' }}>
                                            <p className='overview-label' style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6' }}>
                                                Oświadczam, że jestem osobą pełnoletnią, posiadającą pełną zdolność do czynności prawnych, co jest warunkiem korzystania z Systemu Arrio – PSDiA zgodnie z Regulaminem i Polityką prywatności.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (loadingProfile) {
        return <div>ładowanie profilu...</div>;
    }

    if (!profile) {
        return (
            <div className="student-dashboard-panel">
                <StudentSidebar />
                <div className="main-content">
                    <div className="header">
                        <h1 className="header-title">Mój profil</h1>
                    </div>
                    <div className="dashboard-profile">
                        <p className="student-profile-load-error">
                            Nie udało się wczytać danych profilu. Odśwież stronę lub spróbuj ponownie później.
                        </p>
                    </div>

                </div>
            </div>
        );
    }

    return (
        <div className="student-dashboard-panel">
            {/* Sidebar */}
            <StudentSidebar />
            {/* Main Content */}
            <div className='main-content'>
                {/* Header */}
                <div className='header'>
                    <h1 className='header-title'>Mój profil</h1>
                </div>
                {/* Dashboard Content */}
                <div className="dashboard-profile">
                    {showConsentNotice && (
                        <div className="consent-notice" role="alert">
                            <div className="consent-notice-content">
                                <div className="consent-notice-header">
                                    <FileCheck size={24} />
                                    <div className="consent-notice-main">
                                        <p className="consent-notice-title">Potwierdź wymagane zgody</p>
                                        <p className="consent-notice-text">
                                            Aby kontynuować korzystanie z systemu, zaakceptuj regulamin, politykę prywatności oraz potwierdź pełnoletniość.
                                        </p>
                                    </div>
                                </div>
                                <div className="consent-notice-checkboxes">
                                    <label className="consent-notice-checkbox">
                                        <input
                                            type="checkbox"
                                            className="terms-checkbox"
                                            checked={termsStatus.terms_accepted}
                                            disabled={termsStatus.terms_accepted}
                                            onChange={(e) => handleTermsChange('terms_accepted', e.target.checked)}
                                        />
                                        <span>Akceptuję Regulamin.</span>
                                    </label>
                                    <label className="consent-notice-checkbox">
                                        <input
                                            type="checkbox"
                                            className="terms-checkbox"
                                            checked={termsStatus.privacy_policy_accepted}
                                            disabled={termsStatus.privacy_policy_accepted}
                                            onChange={(e) => handleTermsChange('privacy_policy_accepted', e.target.checked)}
                                        />
                                        <span>Zapoznałem(am) się z Polityką prywatności.</span>
                                    </label>
                                    <label className="consent-notice-checkbox">
                                        <input
                                            type="checkbox"
                                            className="terms-checkbox"
                                            checked={termsStatus.age_of_majority_confirmed}
                                            disabled={termsStatus.age_of_majority_confirmed}
                                            onChange={(e) => handleTermsChange('age_of_majority_confirmed', e.target.checked)}
                                        />
                                        <span>Oświadczam, że mam ukończone 18 lat.</span>
                                    </label>
                                </div>
                                <div>
                                    <label className="consent-notice-text" style={{ fontSize: '14px', color: '#92400e', lineHeight: '1.6', marginTop: '0'}}>
                                        Szczegółowe informacje o zgodach dostępne są w 
                                        <a href="/privacy-policy" target="_blank" style={{ color: '#92400e', fontWeight: 'bold', textDecoration: 'none' }}> Polityce prywatności</a> i 
                                        <a href="/system-regulations" target="_blank" style={{ color: '#92400e', fontWeight: 'bold', textDecoration: 'none' }}> Regulaminie Systemu Arrio – PSDiA</a>.
                                    </label>
                                </div>
                            </div>
                            {activeTab !== 'settings' && (
                                <button
                                    type="button"
                                    className="btn-profile consent-notice-button"
                                    onClick={() => setActiveTab('settings')}
                                >
                                    Więcej informacji
                                </button>
                            )}
                        </div>
                    )}
                    <div className="profile-card">
                        <div className='profile-banner'></div>
                        <div className='profile-section'>
                            <div className='profile-picture' style={{ 
                                backgroundImage: photoUrl ? `url(${photoUrl})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                            }}>
                                {!photoUrl && (
                                    <>
                                        {profile?.first_name?.charAt(0) || ''}
                                        {profile?.last_name?.charAt(0) || ''}
                                    </>
                                )}
                                <div className='profile-edit' onClick={() => setShowEditModal(true)} style={{ cursor: 'pointer' }}>
                                    <Camera size={16} color='white'/>
                                </div>
                            </div>
                            <div className='profile-info'>
                                <h1 className='profile-name'>
                                    {profile?.first_name} {profile?.last_name}
                                </h1>
                                <div className='profile-details'>
                                    Kursant • {profile?.student_number}
                                </div>
                                {loadingStats ? (
                                    <div>ładowanie statystyk...</div>
                                ) : !stats ? (
                                    <div>Brak statystyk do wyświetlenia.</div>
                                ) : (
                                    <div className='profile-stats-row'>
                                        <div className='profile-stat-item'>
                                            <div className='profile-stat-value'>{stats.average_grade}</div>
                                            <div className='profile-stat-label'>Średnia ocen</div>
                                        </div>
                                        <div className='profile-stat-item'>
                                            <div className='profile-stat-value'>{stats.attendance_percentage}%</div>
                                            <div className='profile-stat-label'>Frekwencja</div>
                                        </div>
                                        <div className='profile-stat-item'>
                                            <div className='profile-stat-value'>{stats.assigned_courses}</div>
                                            <div className='profile-stat-label'>Przypisane kursy</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Tabs */}
                    <div className='profile-tabs-container'>
                        <div className='profile-tabs-card'>
                            <div className={`profile-tab ${activeTab === 'overview' ? 'active-tab' : ''}`} onClick={() => setActiveTab('overview')}>
                                <User size={16}/>
                                Przegląd
                            </div>
                            <div className={`profile-tab ${activeTab === 'courses' ? 'active-tab' : ''}`} onClick={() => setActiveTab('courses')}>
                                <BookOpen size={16}/>
                                Moje przedmioty
                            </div>
                            {/* <div className={`profile-tab ${activeTab === 'schedule' ? 'active-tab' : ''}`} onClick={() => setActiveTab('schedule')}>
                                <Calendar size={16}/>
                                Harmonogram
                            </div> */}
                            <div className={`profile-tab ${activeTab === 'settings' ? 'active-tab' : ''}`} onClick={() => setActiveTab('settings')}>
                                <Settings size={16}/>
                                Ustawienia
                            </div>
                        </div>
                        {/* Show content depend of active tab */}
                        <div className="profile-tab-panels">
                            {activeTab === 'overview' && renderOverviewTab()}
                            {activeTab === 'courses' && renderModulesTab()}
                            {activeTab === 'settings' && renderSettingsTab()}
                        </div>
                    </div>
                    {showEditModal && (
                        <EditProfileModal 
                            profile={profile} 
                            user={user} 
                            onClose={() => setShowEditModal(false)} 
                            onProfileUpdate={(updatedProfile, updatedUser) => {
                                setProfile(prev => ({ ...prev, ...updatedProfile }));
                                setUser(prev => ({ ...prev, ...updatedUser }));
                                // Refresh photo after update
                                if (updatedProfile) {
                                    axios.get(`${process.env.REACT_APP_API_URL}/profile/student/photo`, {
                                        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                                        responseType: 'blob'
                                    })
                                    .then(response => {
                                        const blob = response.data;
                                        // Revoke old URL to prevent memory leaks
                                        if (photoUrlRef.current) {
                                            URL.revokeObjectURL(photoUrlRef.current);
                                        }
                                        const url = URL.createObjectURL(blob);
                                        photoUrlRef.current = url;
                                        setPhotoUrl(url);
                                    })
                                    .catch(error => {
                                        if (error.response?.status !== 404) {
                                            console.error("Error fetching photo:", error);
                                        }
                                        // Revoke old URL
                                        if (photoUrlRef.current) {
                                            URL.revokeObjectURL(photoUrlRef.current);
                                            photoUrlRef.current = null;
                                        }
                                        setPhotoUrl(null);
                                    });
                                }
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}